// @flow
import React from 'react';
import { Form, FormControl, FormGroup, Grid, Row, Col, Button, Alert } from 'react-bootstrap';
import { withLastLocation } from 'react-router-last-location';
import { connect } from 'react-redux';
import { isMobile, isIOS } from 'react-device-detect';
import { setAuthStatus, setLandingScrollPosition, setUserName } from '../../redux/actionCreators';
import { getJsonFromUrl, getUrlFromJson } from '../../utils/helpers';
import Api from '../../api/api';
import PasswordReset from './PasswordReset';
import ValidatedField from '../Validation/ValidatedField';
import ChooseAppPlatformModal from '../Dashboard/Profile/ChooseAppPlatformModal';

type Props = {
  authenticated: boolean,
  history: Object,
  location: Object,
  lastLocation: Object,
  handleLogin: (name: string) => void,
  handleChangeAuthStatus: (authenticated: boolean) => void,
  setUserName: (userName: string) => void,
  handleLandingScrollPositionUpdate: (position: string) => void,
};

type State = {
  email: string,
  password: string,
  showPasswordResetModal: boolean,
  showChooseAppPlatformModal: boolean,
  reset_password_token: string,
  message: {
    type: string,
    text: string,
  },
  errors: Array<Object>,
  queryString: string,
};

class Login extends React.Component<Props, State> {
  state = {
    email: '',
    password: '',
    showPasswordResetModal: false,
    showChooseAppPlatformModal: false,
    reset_password_token: '',
    message: {
      type: 'success',
      text: '',
    },
    errors: [],
    queryString: '',
  };

  componentDidMount() {
    if (this.props.authenticated) {
      this.props.handleChangeAuthStatus(false);
    }

    this.props.handleLandingScrollPositionUpdate('');

    this.handleUrlSearchParams();
  }

  componentDidUpdate = (prevProps, prevState) => {
    if (prevProps.location.search !== this.props.location.search) {
      this.handleUrlSearchParams();
    }
  };

  handleUrlSearchParams = () => {
    if (!this.props.location.search) return;

    const urlParams = getJsonFromUrl(this.props.location.search);

    if (urlParams.reset_password_token) {
      if (urlParams.mobile && urlParams.mobile === 'true') {
        const formattedParams = Object.assign({}, urlParams);
        delete formattedParams.mobile;

        const queryString = Object.values(formattedParams).length > 0 ? `?${getUrlFromJson(formattedParams)}` : '';

        if (isMobile && isIOS) {
          return this.setState({ showChooseAppPlatformModal: true, queryString });
        }
      }

      return this.handleResetPasswordToken(urlParams.reset_password_token);
    }
  };

  handleResetPasswordToken = reset_password_token => {
    this.validateToken(reset_password_token);

    this.setState({ showPasswordResetModal: true, reset_password_token: reset_password_token });
  };

  validateToken = token => {
    Api.users
      .resetPassword({
        user: {
          reset_password_token: token,
          password: '',
          password_confirmation: '',
        },
      })
      .catch(error => {
        if (error.response.data.reset_password_token) {
          this.props.history.push('/invalid-link');
        }
      });
  };

  setMessage = (text: string, type: string = 'success') => {
    this.setState({ ...this.state, message: { text, type } });
  };

  clearRouterHistorySearch = () =>
    this.props.history.push({
      pathname: '/login',
      search: '',
    });

  clearPasswordToken = () => this.setState({ reset_password_token: '' });

  handleInputChange = (event: SyntheticInputEvent<HTMLInputElement>) => {
    this.setState({
      [event.target.id]: event.target.value,
    });
  };

  handleLogin = (event: SyntheticEvent<HTMLButtonElement>) => {
    event.preventDefault();

    this.setState({ errors: [] }, () => {
      Api.users
        .login({ user: { email: this.state.email, password: this.state.password } })
        .then(({ first_name, last_name }) => {
          this.props.handleChangeAuthStatus(true);
          this.props.setUserName(`${first_name} ${last_name}`);

          const lastLocation = this.props.lastLocation;

          lastLocation &&
          (lastLocation.pathname === '/suspend-subscription' || lastLocation.pathname.includes('/exercise'))
            ? this.props.history.goBack()
            : this.props.history.push('/profile');
        })
        .catch(() =>
          this.setState({
            errors: [...this.state.errors, { field: 'password', message: "Username and password don't match." }],
          }),
        );
    });
  };

  handlePasswordResetModalClose = () => this.setState({ showPasswordResetModal: false });

  handlePasswordResetModalShow = () => this.setState({ showPasswordResetModal: true });

  handleChooseAppPlatformModalClose = () => {
    this.props.history.push(`/login${this.state.queryString}`);

    this.setState({ showChooseAppPlatformModal: false });
  };

  handleOpenInAppClick = () => {
    this.clearPasswordToken();

    this.clearRouterHistorySearch();

    this.setState({ showChooseAppPlatformModal: false });

    //$FlowFixMe
    window.location.assign(`${process.env.REACT_APP_DEEPLINK_HOST}/login${this.state.queryString}`);
  };

  render() {
    return (
      <Grid className="main-fullheight">
        <div className="fullheight">
          <Row className="show-grid">
            <Col md={12} className="show-grid-in for-table-width">
              {this.state.message.text && (
                <Alert bsStyle={this.state.message.type} id="loginAlert" className="alert-test">
                  <span className="png-ok-circle" />
                  <p>{this.state.message.text}</p>
                </Alert>
              )}
              <h2 className="heading hd-left">Log in to your account</h2>
              <Form onSubmit={this.handleLogin}>
                <FormGroup>
                  <FormControl
                    id="email"
                    type="text"
                    placeholder="Email Address"
                    onChange={this.handleInputChange}
                    value={this.state.email}
                  />
                </FormGroup>
                <FormGroup>
                  <ValidatedField field="password" errors={this.state.errors}>
                    <FormControl
                      id="password"
                      type="password"
                      placeholder="Password"
                      onChange={this.handleInputChange}
                      value={this.state.password}
                    />
                  </ValidatedField>
                </FormGroup>
                <div className="form-footer">
                  <p onClick={this.handlePasswordResetModalShow} className="restore-password">
                    Forgot your password?
                  </p>
                  <Button type="submit" bsStyle="primary">
                    Log in
                  </Button>
                </div>
              </Form>
              <PasswordReset
                show={this.state.showPasswordResetModal}
                reset_password_token={this.state.reset_password_token}
                clearPasswordToken={this.clearPasswordToken}
                handleModalClose={this.handlePasswordResetModalClose}
                setMessage={this.setMessage}
                clearRouterHistorySearch={this.clearRouterHistorySearch}
              />
              <ChooseAppPlatformModal
                show={this.state.showChooseAppPlatformModal}
                onHide={this.handleChooseAppPlatformModalClose}
                handleOpenInAppClick={this.handleOpenInAppClick}
              />
            </Col>
          </Row>
        </div>
      </Grid>
    );
  }
}

const mapStateToProps = state => ({ authenticated: state.session.authenticated });

const mapDispatchToProps = (dispatch: Function) => ({
  handleChangeAuthStatus(authenticated) {
    dispatch(setAuthStatus(authenticated));
  },

  setUserName(userName) {
    dispatch(setUserName(userName));
  },

  handleLandingScrollPositionUpdate(landingScrollPosition) {
    dispatch(setLandingScrollPosition(landingScrollPosition));
  },
});

export default withLastLocation(connect(mapStateToProps, mapDispatchToProps)(Login));



// WEBPACK FOOTER //
// ./src/components/Login/Login.js